import React, { useState } from "react";

const TicketTable = ({ tickets, onRowClick }) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Search logic covering ID, Subject, and Requester Name
  const filteredTickets = tickets.filter(
    (t) =>
      t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.requester?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Open":
        return "bg-blue-100 text-blue-700";
      case "In Progress":
        return "bg-amber-100 text-amber-700";
      case "Resolved":
        return "bg-green-100 text-green-700";
      case "Closed":
        return "bg-slate-100 text-slate-600";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case "High":
        return "text-red-600 bg-red-50 border-red-100";
      case "Urgent":
        return "text-white bg-red-500 border-red-500";
      case "Medium":
        return "text-amber-700 bg-amber-50 border-amber-100";
      default:
        return "text-emerald-700 bg-emerald-50 border-emerald-100";
    }
  };

  return (
    <div className="bg-white rounded-2xl lg:rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden flex flex-col h-full">
      {/* Header & Search */}
      <div className="p-4 sm:p-6 border-b border-slate-50 flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3 sm:gap-4">
        <h2 className="text-base sm:text-lg font-black text-[#0B1C2D] flex items-center gap-2 uppercase tracking-tighter">
          <span className="material-symbols-outlined text-red-500">
            confirmation_number
          </span>
          Support Queue
        </h2>
        <div className="relative w-full lg:w-80">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-[18px]">
            search
          </span>
          <input
            type="text"
            placeholder="Search by ID, Subject or User..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:border-[#0B1C2D] outline-none transition-all font-medium"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden flex-1 divide-y divide-slate-100">
        {filteredTickets.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">
            No tickets found
          </div>
        ) : (
          filteredTickets.map((ticket) => {
            const hasUnreadUserMsg = ticket.messages?.some(
              (msg) => msg.role === "user" && msg.status === "unseen",
            );
            return (
              <button
                key={ticket.id}
                type="button"
                onClick={() => onRowClick(ticket)}
                className={`w-full text-left p-4 active:bg-slate-50 transition-colors ${
                  hasUnreadUserMsg
                    ? "bg-blue-50/40 border-l-4 border-l-red-500"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`font-mono font-black text-[11px] ${hasUnreadUserMsg ? "text-[#0B1C2D]" : "text-slate-400"}`}
                    >
                      {ticket.displayId || ticket.id}
                    </span>
                    {hasUnreadUserMsg && (
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase shrink-0">
                    {formatDate(ticket.lastUpdated)}
                  </span>
                </div>
                <p
                  className={`text-sm text-[#0B1C2D] mb-2 line-clamp-2 ${hasUnreadUserMsg ? "font-black" : "font-semibold"}`}
                >
                  {ticket.subject}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center font-black text-[10px] text-slate-500 uppercase overflow-hidden shrink-0">
                      {ticket.requester?.avatar ? (
                        <img
                          src={ticket.requester.avatar}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        ticket.requester?.name?.charAt(0) || "U"
                      )}
                    </div>
                    <span className="text-xs font-bold text-slate-600 truncate">
                      {ticket.requester?.name || "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase border ${getStatusStyle(ticket.status)}`}
                    >
                      {ticket.status}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase border ${getPriorityStyle(ticket.priority)}`}
                    >
                      {ticket.priority}
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto flex-1 custom-scrollbar">
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-slate-50/50 text-slate-400 font-black uppercase text-[10px] tracking-widest border-b border-slate-50">
            <tr>
              <th className="px-6 py-5">Ticket</th>
              <th className="px-6 py-5">Requester</th>
              <th className="px-6 py-5">Subject</th>
              <th className="px-6 py-5">Status</th>
              <th className="px-6 py-5">Priority</th>
              <th className="px-6 py-5 text-right">Last Activity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredTickets.map((ticket) => {
              const hasUnreadUserMsg = ticket.messages?.some(
                (msg) => msg.role === "user" && msg.status === "unseen",
              );

              return (
                <tr
                  key={ticket.id}
                  onClick={() => onRowClick(ticket)}
                  className={`transition-all cursor-pointer group border-l-4 ${
                    hasUnreadUserMsg
                      ? "bg-blue-50/30 border-l-red-500"
                      : "hover:bg-slate-50 border-l-transparent"
                  }`}
                >
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-mono font-black text-xs ${hasUnreadUserMsg ? "text-[#0B1C2D]" : "text-slate-400"}`}
                      >
                        {ticket.displayId || ticket.id}
                      </span>
                      {hasUnreadUserMsg && (
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-black text-[10px] text-slate-500 uppercase overflow-hidden">
                        {ticket.requester?.avatar ? (
                          <img
                            src={ticket.requester.avatar}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          ticket.requester?.name?.charAt(0) || "U"
                        )}
                      </div>
                      <span
                        className={`text-xs ${hasUnreadUserMsg ? "font-black text-[#0B1C2D]" : "font-bold text-slate-600"}`}
                      >
                        {ticket.requester?.name || "Unknown"}
                      </span>
                    </div>
                  </td>
                  <td
                    className={`px-6 py-5 text-[#0B1C2D] text-xs max-w-[200px] truncate ${hasUnreadUserMsg ? "font-black" : "font-semibold"}`}
                  >
                    {ticket.subject}
                  </td>
                  <td className="px-6 py-5">
                    <span
                      className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-tighter border ${getStatusStyle(ticket.status)}`}
                    >
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span
                      className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase border ${getPriorityStyle(ticket.priority)}`}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-right text-slate-400 text-[10px] font-bold uppercase tracking-tighter">
                    {formatDate(ticket.lastUpdated)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TicketTable;
